import { Component, DestroyRef, inject, OnInit, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NgForm } from "@angular/forms";
import { LookupService, OrganizationService, UserService } from "../../../services/rnapi2-service/apis/api";
import { RnCommonSearch, RnUserTypesVM, RnUserUserTypeAdd } from "../../../services/rnapi2-service/models/models";
import { SelectItem } from "primeng/api";
import { NotificationDialogService } from "../../../services/notificationDialog/notification-dialog.service";
import { GenericDialogConfiguration } from "../generic-dialog/generic-dialog-configuration";
import dayjs from "dayjs";
import { ToasterService, ToastType } from '@rn-platform/frontend-shared-data-access-toaster';
import { ModalComponent } from "@rn-platform/frontend-shared-ui-common";
import { BillingSeparationModalService } from "./billing-separation-modal.service";

@Component({
    selector: "app-billing-separation",
    templateUrl: "./billing-separation.component.html",
    styleUrls: ["./billing-separation.component.scss"],
    standalone: false
})
export class BillingSeparationComponent implements OnInit {
  @ViewChild("modalRef") modalComponent: ModalComponent
  public title = "Convert to Client-Paid Account";
  public theOnlyActiveUser = "";
  public activeUsers: SelectItem[] = [];
  public activeUser: any;
  public oneActiveUser = false;
  public manyActiveUsers = false;
  public anyActiveUsers = false;
  public noActiveUsers = false;  
  public orgName: string;
  public parentName: string;
  public orgId: number;
  public roles?: Array<RnUserTypesVM> | null;
  private newAccountOwner = "";
  private readonly billingSeparationModalService = inject(BillingSeparationModalService);
  private readonly notificationDialogService = inject(NotificationDialogService);
  private readonly toasterService = inject(ToasterService)
  private readonly organizationService = inject(OrganizationService);
  private readonly userService = inject(UserService);
  private readonly lookupService = inject(LookupService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit() {
     this.billingSeparationModalService.modalState$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {      
      if(!data?.config) return;
      this.orgName = data.config.Name;
      this.orgId = data.config.ID;
      this.parentName = data?.config.ParentName ?? '';
      this.loadActiveUsers();
      this.loadUserRoles();
      if (data?.isOpen) {
        this.open();
      } else {
        this.close();
      }
    });
  }

  onAccountOwnerSelect(event) {
    this.activeUser = event.value;
  }

  public loadActiveUsers() {
    const rnCommonSearch: RnCommonSearch = {
      Id: this.orgId.toString(),
      OrderByText: "orderByFullName",
    };
    const message = 'There was an error loading user information.';
    const id = 'apphub-billing-separation-user-load-error-toast';
    this.organizationService
      .apiV2OrganizationsUsersPost(rnCommonSearch)
      .subscribe({
        next: (result) => {
          if (result.Success && result.data) {
            const users = result?.data.Results.filter(
              (x) => x.UserState.toLowerCase() == "active",
            )
              ?.sort((a, b) => {
                if (a.FullName < b.FullName) {
                  return -1;
                }
                else if (a.FullName > b.FullName) {
                  return 1;
                }
                else {
                  return 0;
                }
              });

            users.forEach((obj) => {
              this.activeUsers.push({ label: obj.FullName, value: obj.UserID });
            });

            this.noActiveUsers = users.length == 0;
            this.oneActiveUser = users.length == 1;
            this.manyActiveUsers = users.length > 1;
            this.anyActiveUsers = users.length >= 1;
            if (this.oneActiveUser) {
              this.theOnlyActiveUser =
                users[0].FirstName + " " + users[0].LastName;
              this.activeUser = users[0].UserID;
            }
          } else {
            this.toasterService.addToast(ToastType.Error_, message, message, id, id);
            let errMsg = "There was an error loading user information.";
            if (result && result.Messages && result.Messages.length > 0) {
              for (let i = 0; i < result.Messages.length; i++) {
                const error = result.Messages[i];
                errMsg +=
                  "\r\nCode: " +
                  error.Code +
                  "\r\nName: " +
                  error.Name +
                  "\r\nDescription: " +
                  error.Description;
              }
            }
            console.error(errMsg);
          }
        },
        error: (error) => {
          console.error(error);
          this.toasterService.addToast(ToastType.Error_, message, message, id, id);
        },
      });
  }

  public loadUserRoles() {
    const message = 'There was an error loading user types.';
    const id = 'apphub-billing-separation-user-types-load-error-toast';
    this.lookupService
      .apiV2LookupUsertypesGet(-1, this.orgId)
      .subscribe({
        next: (result) => {
          if (result.Success) {
            this.roles = result.data;
          } else {            
            this.toasterService.addToast(ToastType.Error_, message, message, id, id);
          }
        },
        error: (error) => {
          console.error(error);
          this.toasterService.addToast(ToastType.Error_, message, message, id, id);
        },
      });
  }

  public onSubmit(billingForm: NgForm) {
    if (billingForm.valid) {
      this.newAccountOwner = this.activeUsers.filter(
        (x) => x.value == this.activeUser,
      )[0].label;

      const confirmConfig = new GenericDialogConfiguration();
      confirmConfig.StyleClass = "confirmation";
      confirmConfig.Title = "Convert to Client-Paid Account?";
      confirmConfig.Message = `Please note that this process cannot be stopped once it has started.`;
      confirmConfig.ConfirmButtonStyleClass = "dontCreateUserButton";
      confirmConfig.DialogHeaderClass = "modal-header no-border";
      confirmConfig.DialogFooterCancelClass = "right-spacing";
      confirmConfig.ConfirmButtonText = "CONVERT";
      confirmConfig.CancelButtonText = "CANCEL";
      confirmConfig.CancelButtonStyleClass = "addUserTeritary";
      confirmConfig.confirmButtonNoContentPadding = true;
      confirmConfig.cancelButtonNoContentPadding = true;
      confirmConfig.MessageContainsHTML = false;
      confirmConfig.ShowCloseButton = true;
      confirmConfig.UseProgressButton = true;
      confirmConfig.KeepOpenAfterConfirm = true;
      confirmConfig.Confirmed = (dialogRef) => {
        this.submitClientSeparate();
      };
      this.notificationDialogService.ShowConfirmation(confirmConfig);
    }
  }

  //the function that sets the account owner, and in case of sucess, it triggers billing separatoin
  private submitClientSeparate() {
    let accountOwnerRoleId: number;
    const aoRoles = this.roles.filter((x) => x.Name === "Account Owner");
    if (aoRoles.length > 0) {
      accountOwnerRoleId = aoRoles[0].ID;
    }
    const rnUserUserTypeAdd: RnUserUserTypeAdd = {
      Users_ID: this.activeUser,
      UserType_ID: accountOwnerRoleId
    };
    this.userService.apiV2UsersAddusertypePost(rnUserUserTypeAdd).subscribe({
      next: (result) => {
        if (result.Success) {
          this.BillingSeparate();
          return true;
        } else {
          const message = 'There was an error updating usertypes.';
          const id = 'apphub-billing-separation-user-types-update-error-toast';
          this.toasterService.addToast(ToastType.Error_, message, message, id, id);
          if (result.Messages && result.Messages.length > 0) {
            let errMsg = "";
            for (let i = 0; i < result.Messages.length; i++) {
              const error = result.Messages[i];
              errMsg +=
                "\r\nCode: " +
                error.Code +
                "\r\nName: " +
                error.Name +
                "\r\nDescription: " +
                error.Description;
                this.toasterService.addToast(ToastType.Error_, error.Description, error.Description, id, id);
            }
            console.error(errMsg);
          }
        }
      },
      error: (error) => {
        console.error(error);
        if (error.Messages && error.Messages.length > 0) {
          const id = 'apphub-billing-separation-user-types-update-error-toast';
          this.toasterService.addToast(ToastType.Error_, error.Description, error.Description, id, id);
        }
      },
      complete: () => {
        this.close();
      },
    });
  }

  //the function that communicated with the api for billing separation
  private BillingSeparate() {
    const now = dayjs();
    const values = [now.format()];
    this.organizationService
      .apiV2OrganizationsCreateorupdateorgconfigurationPost(
        this.orgId,
        "InitiatedBillingSeparation",
        values
      )
      .subscribe({
        next: (result) => {
          if (result.Success) {
            // we succeeded, update rootScope config variable
            const id = 'apphub-billing-separation-initiate-separation-success-toast';
            const message =
              'The process to convert ' +
              this.orgName +
              ' to a Client-Paid Account has been successfully initiated. ' +
              this.newAccountOwner +
              ' will receive an email with instructions to establish their billing relationship.';
            this.toasterService.addToast(ToastType.Success_, message, message, id, id);
            setTimeout(() => {
              location.reload();
            }, 6000);
          } else {
            const id = 'apphub-billing-separation-initiate-separation-error-toast';
            const message = 'There was an error beginning the process of client separation for this account.';
            this.toasterService.addToast(ToastType.Error_, message, message, id, id);
          }
        },
        error: () => {
          const id = 'apphub-billing-separation-initiate-separation-error-toast';
          const message = 'There was an error beginning the process of client separation for this account.';
          this.toasterService.addToast(ToastType.Error_, message, message, id, id);
        },
      });
  }

  public open(): void {
    this.modalComponent?.open();
  }

  public close(): void {
    this.modalComponent?.close();
  }
}
